/**
 * NarrativeSummary.js
 * AI-generated narrative briefing cards — Direction C: "Dashboard Card"
 *
 * Tabbed interface with color-coded sections for maximum scannability.
 * Optimized for "10-second comprehension" and clear next action.
 *
 * Exports:
 *   NarrativeSummary       – Entity-level (Status | Impact | Next Step tabs)
 *   ClientNarrativeSummary – Account-level (Executive Brief + Developments/Risks/Actions tabs)
 *   NarrativeSkeleton      – Loading placeholder
 *   NarrativeError         – Inline error state
 */
import React, { useState, useRef, useCallback, useMemo } from 'react';
import {
  RiSparklingLine,
  RiBarChart2Line,
  RiFileTextLine,
  RiLightbulbLine,
  RiFlashlightLine,
  RiLineChartLine,
  RiAlertLine,
  RiErrorWarningLine,
  RiArrowDownSLine,
} from 'react-icons/ri';

/* ─── Utilities ──────────────────────────────────────────────────── */

/** Convert a date string to a human-readable relative time. */
function relativeTime(dateStr) {
  if (!dateStr) return null;
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;
    const now = new Date();
    const diffMs = now - date;
    if (diffMs < 0) return date.toLocaleDateString();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return date.toLocaleDateString();
  } catch {
    return null;
  }
}

/** Safely parse a JSON string into an array. */
function parseJsonArray(val) {
  if (Array.isArray(val)) return val;
  if (typeof val !== 'string') return [];
  try {
    return JSON.parse(val);
  } catch {
    return [];
  }
}

/** Stable unique ID for ARIA attributes (survives re-renders). */
let _idCounter = 0;
function useStableId(prefix = 'nrt') {
  const ref = useRef(null);
  if (!ref.current) ref.current = `${prefix}-${++_idCounter}`;
  return ref.current;
}

/* ─── Accent Configs ─────────────────────────────────────────────── */

const ACCENT = {
  neutral: { border: '#535862', bg: '#f5f5f6', pill: '#535862' },
  cyan: { border: '#009cdc', bg: 'rgba(0, 156, 220, 0.06)', pill: '#009cdc' },
  yellow: { border: '#efa31d', bg: 'rgba(239, 163, 29, 0.07)', pill: '#c4860f' },
  purple: { border: '#6e4fc4', bg: 'rgba(110, 79, 196, 0.06)', pill: '#6e4fc4' },
};

/* ─── Shared Card Styles ─────────────────────────────────────────── */

const CARD_STYLE = {
  backgroundColor: 'var(--pallium-white)',
  boxShadow: 'var(--pallium-shadow-md)',
  border: '1px solid var(--pallium-stroke-light)',
  borderRadius: '16px',
  overflow: 'hidden',
  minWidth: 0,         /* override min-width:auto in flex/grid parent contexts */
  position: 'relative', /* contain absolutely-positioned collapsed body */
};

/* ═══════════════════════════════════════════════════════════════════
   Sub-components
   ═══════════════════════════════════════════════════════════════════ */

/* ─── Tab Bar (pill style, ARIA tablist, keyboard navigable) ───── */

function TabBar({ tabs, activeIndex, onChange, id }) {
  const tabRefs = useRef([]);

  const handleKeyDown = useCallback(
    (e) => {
      const len = tabs.length;
      let next = activeIndex;
      if (e.key === 'ArrowRight') next = (activeIndex + 1) % len;
      else if (e.key === 'ArrowLeft') next = (activeIndex - 1 + len) % len;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = len - 1;
      else return;
      e.preventDefault();
      onChange(next);
      tabRefs.current[next]?.focus();
    },
    [tabs.length, activeIndex, onChange],
  );

  return (
    <div
      role="tablist"
      aria-label="Narrative sections"
      className="flex gap-1 p-1 rounded-lg"
      style={{ backgroundColor: 'var(--pallium-stroke-lighter)' }}
    >
      {tabs.map((tab, i) => {
        const isActive = i === activeIndex;
        const isEmphasized = tab.emphasized && isActive;
        const IconCmp = tab.icon;

        return (
          <button
            key={tab.key}
            ref={(el) => {
              tabRefs.current[i] = el;
            }}
            role="tab"
            id={`${id}-tab-${i}`}
            aria-selected={isActive}
            aria-controls={`${id}-panel-${i}`}
            tabIndex={isActive ? 0 : -1}
            onKeyDown={handleKeyDown}
            onClick={() => onChange(i)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer select-none whitespace-nowrap"
            style={{
              transition: 'all 0.15s ease-in-out',
              border: 'none',
              backgroundColor: isActive
                ? isEmphasized
                  ? 'var(--pallium-primary-purple)'
                  : 'var(--pallium-white)'
                : 'transparent',
              color: isActive
                ? isEmphasized
                  ? 'var(--pallium-white)'
                  : 'var(--pallium-text-primary)'
                : 'var(--pallium-text-secondary)',
              boxShadow: isActive && !isEmphasized ? 'var(--pallium-shadow-xs)' : 'none',
            }}
          >
            {IconCmp && <IconCmp style={{ width: 14, height: 14, flexShrink: 0 }} />}
            <span>{tab.label}</span>
            {tab.count != null && (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  borderRadius: 9999,
                  paddingLeft: 6,
                  paddingRight: 6,
                  lineHeight: '16px',
                  minWidth: 18,
                  textAlign: 'center',
                  backgroundColor: isActive
                    ? isEmphasized
                      ? 'rgba(255,255,255,0.25)'
                      : `${tab.accentColor || tab.accent.pill}18`
                    : 'var(--pallium-stroke-light)',
                  color: isActive
                    ? isEmphasized
                      ? 'var(--pallium-white)'
                      : tab.accentColor || tab.accent.pill
                    : 'var(--pallium-text-secondary)',
                }}
              >
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* ─── Section Panel (color-accented content area) ────────────────── */

function SectionPanel({ accent, id, labelledBy, children }) {
  return (
    <div
      role="tabpanel"
      id={id}
      aria-labelledby={labelledBy}
      tabIndex={0}
      className="rounded-lg"
      style={{
        borderLeft: `3px solid ${accent.border}`,
        backgroundColor: accent.bg,
        padding: '14px 16px',
      }}
    >
      {children}
    </div>
  );
}

/* ─── Numbered Action Item ───────────────────────────────────────── */

function ActionItem({ index, children }) {
  return (
    <div className="flex gap-3 items-start" style={{ padding: '4px 0' }}>
      <span
        className="flex-shrink-0 flex items-center justify-center rounded-full"
        style={{
          width: 20,
          height: 20,
          fontSize: 10,
          fontWeight: 700,
          marginTop: 2,
          backgroundColor: 'var(--pallium-primary-purple)',
          color: 'var(--pallium-white)',
        }}
      >
        {index}
      </span>
      <span
        className="text-sm"
        style={{ color: 'var(--pallium-text-primary)', lineHeight: 1.625 }}
      >
        {children}
      </span>
    </div>
  );
}

/* ─── Custom Bullet List ─────────────────────────────────────────── */

function BulletList({ items, accentColor }) {
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.map((item, i) => (
        <li
          key={i}
          className="flex items-start text-sm"
          style={{ color: 'var(--pallium-text-primary)', lineHeight: 1.625, gap: 10 }}
        >
          <span
            className="flex-shrink-0 rounded-full"
            style={{
              width: 6,
              height: 6,
              marginTop: 7,
              backgroundColor: accentColor,
            }}
          />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

/* ─── Card Header ────────────────────────────────────────────────── */

function CardHeader({ id, icon: Icon, title, expanded, onToggle, timeLabel }) {
  return (
    <div className="flex items-center justify-between" style={{ padding: '16px 20px 8px' }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={`${id}-body`}
        className="flex items-center cursor-pointer"
        style={{
          gap: 8,
          background: 'none',
          border: 'none',
          padding: 0,
          margin: 0,
          color: 'var(--pallium-text-primary)',
          lineHeight: 1,
        }}
      >
        <Icon
          style={{
            width: 18,
            height: 18,
            flexShrink: 0,
            color: 'var(--pallium-primary-purple)',
          }}
        />
        <span className="font-semibold" style={{ fontSize: 14 }}>
          {title}
        </span>
        <RiArrowDownSLine
          style={{
            width: 16,
            height: 16,
            flexShrink: 0,
            color: 'var(--pallium-icon-gray)',
            transition: 'transform 0.2s ease',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </button>
      {timeLabel && (
        <span style={{ fontSize: 12, color: 'var(--pallium-icon-gray)' }}>
          Updated {timeLabel}
        </span>
      )}
    </div>
  );
}

/* ─── Collapsible Body (max-height animation) ───────────────────── */

function CollapsibleBody({ id, expanded, children }) {
  return (
    <div
      id={`${id}-body`}
      role="region"
      aria-label="Narrative details"
      style={{
        overflow: 'hidden',
        maxHeight: expanded ? 2000 : 0,
        opacity: expanded ? 1 : 0,
        transition: 'max-height 0.3s ease, opacity 0.2s ease',
        /* When collapsed, prevent hidden content from inflating parent width */
        ...(expanded ? {} : { position: 'absolute', visibility: 'hidden', width: '100%' }),
      }}
    >
      <div style={{ padding: '4px 20px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {children}
      </div>
    </div>
  );
}

/* ─── Collapsed Peek Line ────────────────────────────────────────── */

function PeekLine({ text }) {
  if (!text) return null;
  return (
    <div style={{ padding: '0 20px 12px', overflow: 'hidden' }}>
      <p
        style={{
          fontSize: 12,
          lineHeight: 1.5,
          margin: 0,
          color: 'var(--pallium-text-secondary)',
          display: '-webkit-box',
          WebkitLineClamp: 1,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {text}
      </p>
    </div>
  );
}

/* ─── Powered-by Footer ──────────────────────────────────────────── */

function PoweredBy() {
  return (
    <div className="flex items-center" style={{ gap: 4, paddingTop: 4 }}>
      <RiSparklingLine style={{ width: 12, height: 12, color: 'var(--pallium-icon-gray)' }} />
      <span style={{ fontSize: 10, color: 'var(--pallium-icon-gray)' }}>
        Generated by Pallium AI
      </span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   Loading Skeleton
   ═══════════════════════════════════════════════════════════════════ */

export function NarrativeSkeleton({ variant = 'entity' }) {
  return (
    <div className="mt-4 animate-pulse" style={CARD_STYLE}>
      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Header row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center" style={{ gap: 8 }}>
            <div className="rounded" style={{ width: 18, height: 18, backgroundColor: '#e5e7eb' }} />
            <div className="rounded" style={{ height: 16, width: 112, backgroundColor: '#e5e7eb' }} />
          </div>
          <div className="rounded" style={{ height: 12, width: 64, backgroundColor: '#f3f4f6' }} />
        </div>
        {/* Brief (client only) */}
        {variant === 'client' && (
          <div className="rounded-lg" style={{ backgroundColor: '#f4f4f4', padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div className="rounded" style={{ height: 12, width: 80, backgroundColor: '#e5e7eb' }} />
            <div className="rounded" style={{ height: 12, width: '100%', backgroundColor: '#e5e7eb' }} />
            <div className="rounded" style={{ height: 12, width: '83%', backgroundColor: '#f3f4f6' }} />
          </div>
        )}
        {/* Tab bar */}
        <div className="flex rounded-lg" style={{ gap: 4, padding: 4, backgroundColor: '#f4f4f4' }}>
          <div className="rounded-md" style={{ height: 28, width: 80, backgroundColor: '#e5e7eb' }} />
          <div className="rounded-md" style={{ height: 28, width: 80, backgroundColor: '#f3f4f6' }} />
          <div className="rounded-md" style={{ height: 28, width: 96, backgroundColor: '#f3f4f6' }} />
        </div>
        {/* Panel */}
        <div className="rounded-lg" style={{ padding: 16, backgroundColor: '#f4f4f4', borderLeft: '3px solid #e5e7eb', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div className="rounded" style={{ height: 12, width: '100%', backgroundColor: '#e5e7eb' }} />
          <div className="rounded" style={{ height: 12, width: '80%', backgroundColor: '#f3f4f6' }} />
          <div className="rounded" style={{ height: 12, width: '60%', backgroundColor: '#f3f4f6' }} />
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   Error State
   ═══════════════════════════════════════════════════════════════════ */

export function NarrativeError({ title = 'AI Briefing', icon: Icon = RiSparklingLine }) {
  return (
    <div className="mt-4" style={CARD_STYLE}>
      <div style={{ padding: 20 }}>
        <div className="flex items-center" style={{ gap: 8, marginBottom: 12 }}>
          <Icon style={{ width: 18, height: 18, color: 'var(--pallium-primary-purple)' }} />
          <span className="font-semibold" style={{ fontSize: 14, color: 'var(--pallium-text-primary)' }}>
            {title}
          </span>
        </div>
        <div
          className="flex items-center rounded-lg"
          style={{
            gap: 8,
            padding: '8px 12px',
            fontSize: 14,
            color: 'var(--pallium-text-secondary)',
            backgroundColor: 'rgba(239, 163, 29, 0.07)',
          }}
        >
          <RiErrorWarningLine
            style={{ width: 16, height: 16, flexShrink: 0, color: 'var(--pallium-warning-yellow)' }}
          />
          <span>Unable to load AI summary. It will refresh automatically.</span>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   NarrativeSummary — Entity-level
   (Goals, Initiatives, Issues, Action Items)
   ═══════════════════════════════════════════════════════════════════ */

export function NarrativeSummary({
  whatsHappening,
  whyItMatters,
  whatToDo,
  updatedAt,
  defaultExpanded = true,
  title = 'AI Briefing',
}) {
  const id = useStableId('entity');
  const [expanded, setExpanded] = useState(defaultExpanded);

  /* Build tabs from available content */
  const tabs = useMemo(() => {
    const t = [];
    if (whatsHappening)
      t.push({
        key: 'status',
        label: 'Status',
        icon: RiFileTextLine,
        accent: ACCENT.neutral,
        content: whatsHappening,
      });
    if (whyItMatters)
      t.push({
        key: 'impact',
        label: 'Impact',
        icon: RiLightbulbLine,
        accent: ACCENT.cyan,
        content: whyItMatters,
      });
    if (whatToDo)
      t.push({
        key: 'action',
        label: 'Next Step',
        icon: RiFlashlightLine,
        accent: ACCENT.purple,
        content: whatToDo,
        emphasized: true,
        accentColor: ACCENT.purple.pill,
      });
    return t;
  }, [whatsHappening, whyItMatters, whatToDo]);

  const [activeTab, setActiveTab] = useState(0);

  if (tabs.length === 0) return null;

  const safeActive = Math.min(activeTab, tabs.length - 1);
  const current = tabs[safeActive];
  const timeLabel = relativeTime(updatedAt);
  const peekText = tabs[0]?.content;

  return (
    <div className="mt-4" style={CARD_STYLE}>
      <CardHeader
        id={id}
        icon={RiSparklingLine}
        title={title}
        expanded={expanded}
        onToggle={() => setExpanded((e) => !e)}
        timeLabel={timeLabel}
      />

      {/* Collapsed peek */}
      {!expanded && <PeekLine text={peekText} />}

      {/* Expanded body */}
      <CollapsibleBody id={id} expanded={expanded}>
        {/* Tab bar (skip if only one section) */}
        {tabs.length > 1 && (
          <TabBar tabs={tabs} activeIndex={safeActive} onChange={setActiveTab} id={id} />
        )}

        {/* Active panel */}
        <SectionPanel
          accent={current.accent}
          id={`${id}-panel-${safeActive}`}
          labelledBy={tabs.length > 1 ? `${id}-tab-${safeActive}` : undefined}
        >
          {current.key === 'action' ? (
            <ActionItem index={1}>{current.content}</ActionItem>
          ) : (
            <p
              style={{
                fontSize: 14,
                lineHeight: 1.625,
                margin: 0,
                color: 'var(--pallium-text-primary)',
              }}
            >
              {current.content}
            </p>
          )}
        </SectionPanel>

        <PoweredBy />
      </CollapsibleBody>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   ClientNarrativeSummary — Account-level
   (Overview dashboard)
   ═══════════════════════════════════════════════════════════════════ */

export function ClientNarrativeSummary({
  accountSummary,
  keyDevelopments,
  riskAreas,
  recommendedActions,
  updatedAt,
  defaultExpanded = true,
  title = 'Account Briefing',
}) {
  const id = useStableId('client');
  const [expanded, setExpanded] = useState(defaultExpanded);

  const keyList = useMemo(() => parseJsonArray(keyDevelopments), [keyDevelopments]);
  const riskList = useMemo(() => parseJsonArray(riskAreas), [riskAreas]);
  const actionList = useMemo(() => parseJsonArray(recommendedActions), [recommendedActions]);

  /* Build tabs from list sections */
  const tabs = useMemo(() => {
    const t = [];
    if (keyList.length > 0)
      t.push({
        key: 'developments',
        label: 'Developments',
        icon: RiLineChartLine,
        accent: ACCENT.cyan,
        items: keyList,
        count: keyList.length,
        accentColor: ACCENT.cyan.pill,
      });
    if (riskList.length > 0)
      t.push({
        key: 'risks',
        label: 'Risks',
        icon: RiAlertLine,
        accent: ACCENT.yellow,
        items: riskList,
        count: riskList.length,
        accentColor: ACCENT.yellow.pill,
      });
    if (actionList.length > 0)
      t.push({
        key: 'actions',
        label: 'Actions',
        icon: RiFlashlightLine,
        accent: ACCENT.purple,
        items: actionList,
        count: actionList.length,
        emphasized: true,
        accentColor: ACCENT.purple.pill,
      });
    return t;
  }, [keyList, riskList, actionList]);

  const [activeTab, setActiveTab] = useState(0);

  const hasContent =
    accountSummary || keyList.length > 0 || riskList.length > 0 || actionList.length > 0;

  if (!hasContent) return null;

  const safeActive = Math.min(activeTab, Math.max(tabs.length - 1, 0));
  const current = tabs[safeActive];
  const timeLabel = relativeTime(updatedAt);
  const peekText = accountSummary;

  return (
    <div className="mt-4" style={CARD_STYLE}>
      <CardHeader
        id={id}
        icon={RiBarChart2Line}
        title={title}
        expanded={expanded}
        onToggle={() => setExpanded((e) => !e)}
        timeLabel={timeLabel}
      />

      {/* Collapsed peek */}
      {!expanded && <PeekLine text={peekText} />}

      {/* Expanded body */}
      <CollapsibleBody id={id} expanded={expanded}>
        {/* Executive Brief (always shown above tabs) */}
        {accountSummary && (
          <div
            className="rounded-lg"
            style={{ backgroundColor: 'var(--pallium-stroke-lighter)', padding: 16 }}
          >
            <h4
              style={{
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginTop: 0,
                marginBottom: 8,
                color: 'var(--pallium-text-secondary)',
              }}
            >
              Executive Brief
            </h4>
            <p
              style={{
                fontSize: 14,
                lineHeight: 1.625,
                margin: 0,
                color: 'var(--pallium-text-primary)',
              }}
            >
              {accountSummary}
            </p>
          </div>
        )}

        {/* Tabs + active panel */}
        {tabs.length > 0 && (
          <>
            {tabs.length > 1 && (
              <TabBar tabs={tabs} activeIndex={safeActive} onChange={setActiveTab} id={id} />
            )}

            {current && (
              <SectionPanel
                accent={current.accent}
                id={`${id}-panel-${safeActive}`}
                labelledBy={tabs.length > 1 ? `${id}-tab-${safeActive}` : undefined}
              >
                {current.key === 'actions' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {current.items.map((item, i) => (
                      <ActionItem key={i} index={i + 1}>
                        {item}
                      </ActionItem>
                    ))}
                  </div>
                ) : (
                  <BulletList items={current.items} accentColor={current.accent.border} />
                )}
              </SectionPanel>
            )}
          </>
        )}

        <PoweredBy />
      </CollapsibleBody>
    </div>
  );
}

export default NarrativeSummary;
